import { standardTimeToUtcZeroTime } from '@/common/utils/commonUtils';
import { WidgetChartData, WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import type {
  WidgetProps as Props,
  WidgetChartDataWithIndex,
} from '@/dashboard/features/widgets/common/types/widgets.types';
import { MetricV7Request } from '@/api-client/data/model';
import dayjs from 'dayjs';
import { isOverToday } from '../utils/compareDate.utils';
import { resolveCompareDateFromChartOption } from '../domain/compareDateCalculator';
import type { OneDayCompareChartOption } from '../../settings/types/oneDayCompareSettings.types';
import type { WidgetUUID } from '@/common/utils';
import type { ArgParam } from '@/dashboard/features/widgetsAPI/domain/referenceParam';
import type { ReferenceInfo } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/referenceSettingOption.setup';

export interface OneDayCompareTimeWindow {
  fromTime: string;
  toTime: string;
}

export const buildOneDayCompareTimeWindows = (
  compareDateOption: OneDayCompareChartOption['compareDate'] | undefined,
  today: Date,
): OneDayCompareTimeWindow[] => {
  const compareDate = new Date(resolveCompareDateFromChartOption(compareDateOption, today));
  const todayDayjs = dayjs.tz(today);
  const compareDay = dayjs(compareDate).startOf('day');

  const todayWindow: OneDayCompareTimeWindow = {
    fromTime: standardTimeToUtcZeroTime(todayDayjs.format('YYYY-MM-DD HH:mm:ss')),
    toTime: standardTimeToUtcZeroTime(todayDayjs.add(1, 'day').format('YYYY-MM-DD HH:mm:ss')),
  };

  if (isOverToday(compareDate, today)) {
    return [todayWindow];
  }

  const compareWindow: OneDayCompareTimeWindow = {
    fromTime: standardTimeToUtcZeroTime(compareDay.format('YYYY-MM-DD HH:mm:ss')),
    toTime: standardTimeToUtcZeroTime(compareDay.add(1, 'day').format('YYYY-MM-DD HH:mm:ss')),
  };
  return [todayWindow, compareWindow];
};

export interface ApiParamsAdapterDeps {
  getFilteredTags: (params: {
    targets: WidgetChartData['targets'];
    widgetUUID: string;
    chartDataId: string;
  }) => Record<string, unknown>;
  getArgParams: (params: {
    chartType: WidgetChartType;
    chartDataIndex: number;
    chartDataId: string;
    statId: string;
    category: string;
    widgetId: WidgetUUID;
    referenceInfo?: ReferenceInfo;
  }) => ArgParam[] | undefined;
  setArgParamsByWidget: (params: {
    widgetId: WidgetUUID;
    argParams: ArgParam[] | undefined;
  }) => void;
  setPrometheusGlobalVariables: (query: string) => string;
}

export const createChartDataToMetricRequestConverter = (
  props: Props<'ONE_DAY_COMPARE'>,
  interval: 'I10m' | 'I1h',
  deps: ApiParamsAdapterDeps,
) => {
  return (chartData: WidgetChartData) =>
    ({
      fromTime,
      toTime,
      chartDataIndex,
    }: {
      fromTime: string;
      toTime: string;
      chartDataIndex: number;
    }) => {
      const { dataType, dataId, seriesType, targets, category, externalOptions } = chartData;
      const widgetArgParams = deps.getArgParams({
        chartType: props.chartType,
        chartDataIndex,
        chartDataId: chartData.id,
        statId: dataId,
        category,
        widgetId: props.widgetId,
      });

      if (props.createType === 'reference' && props.mode !== 'preview') {
        deps.setArgParamsByWidget({
          widgetId: props.widgetId,
          argParams: widgetArgParams,
        });
      }

      return {
        id: chartData.id,
        dataId: dataId ?? '',
        category,
        aggregationType: seriesType ?? 'avg',
        summaryType: dataType ?? 'current',
        ...deps.getFilteredTags({ targets, widgetUUID: props.widgetId, chartDataId: chartData.id }),
        fromTime,
        toTime,
        interval,
        argParam: widgetArgParams,
        extraResourceOption: externalOptions
          ? {
              legend: externalOptions.labelFormat,
              query: deps.setPrometheusGlobalVariables(externalOptions.query),
              type: externalOptions.labelFormatType,
            }
          : undefined,
      };
    };
};

export const createMetricRequests = (
  widgetChartData: WidgetChartDataWithIndex[],
  compareDateOption: OneDayCompareChartOption['compareDate'] | undefined,
  today: Date,
  props: Props<'ONE_DAY_COMPARE'>,
  interval: 'I10m' | 'I1h',
  deps: ApiParamsAdapterDeps,
): (MetricV7Request & { id: string })[] => {
  const metricRequests: (MetricV7Request & { id: string })[] = [];
  const firstWidgetChartData = widgetChartData.at(0);

  if (!firstWidgetChartData || firstWidgetChartData.seriesType === 'byTarget') {
    return metricRequests;
  }

  const convertor = createChartDataToMetricRequestConverter(
    props,
    interval,
    deps,
  )(widgetChartData[0]);

  buildOneDayCompareTimeWindows(compareDateOption, today).forEach((window) => {
    metricRequests.push(
      convertor({
        ...window,
        chartDataIndex: 0,
      }),
    );
  });

  return metricRequests;
};
